import { FLAT_TRIM_HALF_WIDTH } from '../../surround-material/constants';
import { findSeamConnectionAtAnyGap } from '../helpers';
import { PanelTrim } from '../panel-trim.class';
import { PanelTrimType } from '../enums';
import { degrees2radians, RenderDetails, Vertex2 } from '../../../spatial-classes';

import type { Island } from '../../islands/island.class';
import type { Panel } from '../../panels/panel.class';

type FlatTrimVerticesParams = {
	leftPanel: Panel;
	rightPanel: Panel;
	island: Island | null;
	origin: Vertex2;
	angle: number;
	thickness: number;
};

// a flat trim keeps only the leg along its OWN face panel (panels[0] unless that panel carries the island
// connection), shifted toward the seam by padding/2 at ANY gap width so the two halves meet at gap centre
const getFlatTrimVertices = (params: FlatTrimVerticesParams): Array<Vertex2> => {
	const { leftPanel, rightPanel, island, origin, angle, thickness } = params;
	const seam = island ? findSeamConnectionAtAnyGap([leftPanel, rightPanel], island) : null;
	const isLeftPanelFace = !seam || seam.connectedPanel.id !== leftPanel.id;
	const halfPadding = (seam?.padding ?? 0) / 2;

	// leg along the face panel (same +x direction as the ninety-degree case's outer leg), shifted by
	// -halfPadding so it starts halfPadding into the gap instead of exactly at this island's own edge
	if (isLeftPanelFace) {
		return [
			new Vertex2({ dataIn: { x: -halfPadding, y: 0 }}).rotateAxis(origin, angle),
			new Vertex2({ dataIn: { x: FLAT_TRIM_HALF_WIDTH - halfPadding, y: 0 }}).rotateAxis(origin, angle),
			new Vertex2({ dataIn: { x: FLAT_TRIM_HALF_WIDTH - halfPadding, y: thickness }}).rotateAxis(origin, angle),
			new Vertex2({ dataIn: { x: -halfPadding, y: thickness }}).rotateAxis(origin, angle)
		];
	}

	// leg along the face panel when it's panels[1] instead (same -y direction as the ninety-degree case's
	// other leg), shifted by +halfPadding for the same reason
	return [
		new Vertex2({ dataIn: { x: 0, y: halfPadding }}).rotateAxis(origin, angle),
		new Vertex2({ dataIn: { x: 0, y: halfPadding - FLAT_TRIM_HALF_WIDTH }}).rotateAxis(origin, angle),
		new Vertex2({ dataIn: { x: -thickness, y: halfPadding - FLAT_TRIM_HALF_WIDTH }}).rotateAxis(origin, angle),
		new Vertex2({ dataIn: { x: -thickness, y: halfPadding }}).rotateAxis(origin, angle)
	];
};

export const getRenderingDetailsCfn = (panelTrim: PanelTrim): RenderDetails => {
	const width = 2.5;
	const thickness = 0.5;
	const angle = panelTrim.axis.y;

	// use panelTrim center
	const origin = new Vertex2();
	// const { center } = panelTrim;
	// const offset = new Vertex2({ dataIn: { x: center.x, y: center.z }});

	// locate the left and right panels
	const [leftPanelId, rightPanelId] = panelTrim.panels;
	const { island } = panelTrim.refMap;
	const leftPanel = island?.panels.findById(leftPanelId);
	const rightPanel = island?.panels.findById(rightPanelId);

	// use the tallest panel for reference (they should always be the same height, but...)
	const depth = Math.max(leftPanel?.size.height, rightPanel?.size.height) || 0;
	const tAdj = {
		22.5: Math.tan(degrees2radians(22.5)) * thickness,
		45: Math.tan(degrees2radians(45)) * thickness
	};

	// initialize points array
	let vertices: Array<Vertex2> = [];

	switch (panelTrim.trimType) {
		// ninty degree trims
		case PanelTrimType.ninety:
			vertices = [
				origin,
				new Vertex2({ dataIn: { x: width - thickness, y: 0 }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: width - thickness, y: thickness }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: -thickness, y: thickness }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: -thickness, y: -width + thickness }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: 0, y: -width + thickness }}).rotateAxis(origin, angle)
			];
			break;
		// fortyfive degree trims used on corner islands
		case PanelTrimType.fortyfive:
			vertices = [
				origin,
				new Vertex2({ dataIn: { x: width - thickness, y: 0 }})
			];
			vertices[2] = vertices[1].clone().add(new Vertex2({ dataIn: { y: thickness }}))
			vertices[3] = origin.clone().add(new Vertex2({ dataIn: { x: -tAdj[22.5], y: thickness }}));
			vertices[4] = vertices[3].clone().moveOnAxis((width - thickness + tAdj[22.5]), 225);
			vertices[5] = vertices[4].clone().moveOnAxis(tAdj[45], 315);

			vertices.forEach((vertex, i) => {
				if (i !== 0) vertex.rotateAxis(origin, angle);
			});
			// 	.rotateAxis(origin, angle);

			// vertices = [
			// 	origin,
			// 	new Vertex2({ dataIn: { x: width - thickness, y: 0 }}).rotateAxis(origin, angle),
			// 	new Vertex2({ dataIn: { x: width - thickness, y: thickness }}).rotateAxis(origin, angle),
			// 	new Vertex2({ dataIn: { x: -thickness, y: thickness }}).rotateAxis(origin, angle + 22.5),
			// 	new Vertex2({ dataIn: { x: -thickness, y: -width + thickness }}).rotateAxis(origin, angle + 45),
			// 	new Vertex2({ dataIn: { x: 0, y: -width + thickness }}).rotateAxis(origin, angle + 45)
			// ];
			break;
		// flat trim draws only its OWN 1.5" half along the face panel - the other half belongs to the neighbour
		case PanelTrimType.straight:
			vertices = leftPanel && rightPanel
				? getFlatTrimVertices({ leftPanel, rightPanel, island: island ?? null, origin, angle, thickness })
				: [
					origin,
					new Vertex2({ dataIn: { x: FLAT_TRIM_HALF_WIDTH, y: 0 }}).rotateAxis(origin, angle),
					new Vertex2({ dataIn: { x: FLAT_TRIM_HALF_WIDTH, y: thickness }}).rotateAxis(origin, angle),
					new Vertex2({ dataIn: { x: 0, y: thickness }}).rotateAxis(origin, angle)
				];
			break;
		// unset trim type keeps the legacy full-width strip
		default:
			vertices = [
				origin,
				new Vertex2({ dataIn: { x: width, y: 0 }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: width, y: thickness }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: -width, y: thickness }}).rotateAxis(origin, angle),
				new Vertex2({ dataIn: { x: -width, y: 0 }}).rotateAxis(origin, angle)
			];
	}


	const v = {
		// vertices: vertices.map((vertex) => vertex.add(offset)),
		vertices,
		depth,
		layer: panelTrim.instanceOf
	};

	// console.log('panelTrim.renderDetails', angle, leftPanel.label, leftPanel.getOriginAngle());
	return v;
}
